import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PinPad } from '@renderer/components/PinPad';
import { OnboardingPaso4Schema } from '@shared/validators';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  onSiguiente: () => void;
  onAtras: () => void;
}

type Etapa = 'datos' | 'pin' | 'pin_confirmacion';

export function Step4Dueno({ onSiguiente, onAtras }: Props) {
  const [etapa, setEtapa] = useState<Etapa>('datos');
  const [nombre, setNombre] = useState('');
  const [pin, setPin] = useState('');
  const [, setPinConfirmacion] = useState('');
  const [emailSoporte, setEmailSoporte] = useState('');
  const [consentimiento, setConsentimiento] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);

  const irAPinSetup = () => {
    if (nombre.trim().length < 2) {
      setError('Tu nombre debe tener al menos 2 caracteres');
      return;
    }
    setError(null);
    setEtapa('pin');
  };

  const onPinComplete = (valor: string) => {
    if (etapa === 'pin') {
      setPin(valor);
      setResetSignal((v) => v + 1);
      setEtapa('pin_confirmacion');
    } else if (etapa === 'pin_confirmacion') {
      setPinConfirmacion(valor);
      finalizarPaso(valor);
    }
  };

  const finalizarPaso = async (pinConfirmado: string) => {
    setError(null);
    const data = {
      nombre,
      pin,
      pinConfirmacion: pinConfirmado,
      emailSoporte: emailSoporte || undefined,
      consentimientoTelemetria: consentimiento,
    };
    const parsed = OnboardingPaso4Schema.safeParse(data);
    if (!parsed.success) {
      const msg = parsed.error.errors[0]?.message ?? 'Datos inválidos';
      setError(msg);
      // Volver a etapa pin para reintentar
      setEtapa('pin');
      setPin('');
      setPinConfirmacion('');
      setResetSignal((v) => v + 1);
      return;
    }
    setEnviando(true);
    const res = await window.api.onboarding.guardarPaso(4, parsed.data);
    setEnviando(false);
    if (res.ok) onSiguiente();
    else {
      setError(traducirErrorBackend(res.error, res.codigo));
      setEtapa('pin');
      setPin('');
      setResetSignal((v) => v + 1);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Crea tu usuario de dueño</h1>
        <p className="mt-2 text-slate-500">
          Este PIN te dará acceso a reportes, configuración y acciones sensibles.
        </p>
      </div>

      {etapa === 'datos' && (
        <div className="card space-y-4">
          <div>
            <label className="label" htmlFor="nombreDueno">Tu nombre *</label>
            <input
              id="nombreDueno"
              className="input"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Carlos Méndez"
              autoFocus
            />
          </div>

          <div>
            <label className="label" htmlFor="emailSoporte">Email para reportes de bugs (opcional)</label>
            <input
              id="emailSoporte"
              type="email"
              className="input"
              value={emailSoporte}
              onChange={(e) => setEmailSoporte(e.target.value)}
              placeholder="tu@email.com"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentimiento}
              onChange={(e) => setConsentimiento(e.target.checked)}
              className="mt-1 h-4 w-4"
            />
            <span className="text-sm text-slate-700">
              Permitir envío automático de reportes de error.
              <br />
              <span className="text-xs text-slate-500">
                Nos ayuda a mejorar la app. No incluye datos de tus clientes ni ventas.
              </span>
            </span>
          </label>

          {error && <div className="error-text">{error}</div>}

          <button onClick={irAPinSetup} className="btn-primary btn-lg w-full">
            Continuar a crear PIN
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      )}

      {(etapa === 'pin' || etapa === 'pin_confirmacion') && (
        <div className="card flex flex-col items-center gap-4">
          <PinPad
            label={etapa === 'pin' ? 'Crea tu PIN de 4 dígitos' : 'Confirma tu PIN'}
            onComplete={onPinComplete}
            error={error ?? undefined}
            resetSignal={resetSignal}
          />
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => {
            if (etapa === 'datos') onAtras();
            else {
              setEtapa('datos');
              setPin('');
              setPinConfirmacion('');
              setError(null);
            }
          }}
          className="btn-ghost btn-lg"
          disabled={enviando}
        >
          <ArrowLeft className="h-5 w-5" />
          Atrás
        </button>
        <div /> {/* Sin "Saltar" — paso obligatorio */}
      </div>
    </div>
  );
}
